package com.evoapps.evoshift.domain.privacy

import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.ShiftManagerNotes
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.AccessLevel
import com.evoapps.evoshift.domain.model.Feature
import com.evoapps.evoshift.domain.model.can
import com.evoapps.evoshift.domain.model.withManagerNotes
import com.evoapps.evoshift.domain.repository.ShiftRepository
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.emitAll
import kotlinx.coroutines.flow.flow

/**
 * SH-259: the managers' notes on shifts (why nobody came, why a shift was split) live in their own
 * documents ([ShiftManagerNotes]), which only the workplace's managers and owner may read. For them
 * this puts the notes back on the month's shifts, so every screen reads `Shift.noShowReason` and
 * `ShiftSplit.reason` as before; a note missing there keeps what an older app left on the shift.
 * Everyone else reads the shifts as they are (asking for the notes would only be refused).
 */
class ManagerNotesShiftRepository(
    private val inner: ShiftRepository,
    /** The signed-in person (null: nobody). */
    private val viewer: suspend () -> User?
) : ShiftRepository by inner {

    /**
     * Whether the signed-in person sees the roster of [workplaceId] as a manager (SH-270: roster
     * view, as firestore.rules shift_manager_notes): they may read its notes.
     */
    private suspend fun managesAt(workplaceId: String): Boolean {
        val engagement = runCatching { viewer() }.getOrNull()?.engagements?.get(workplaceId) ?: return false
        return engagement.can(Feature.ROSTER, AccessLevel.VIEW)
    }

    private suspend fun managesRoster(rosterId: String): Boolean =
        rosterId.isNotEmpty() && managesAt(RosterIds.workplaceIdOf(rosterId))

    override fun observeShiftsForRoster(rosterId: String): Flow<List<Shift>> = flow {
        if (!managesRoster(rosterId)) {
            emitAll(inner.observeShiftsForRoster(rosterId))
            return@flow
        }
        // The notes failing (offline before the first read, a role changed meanwhile) never hides
        // the roster: the shifts then show what they carry.
        val notes = inner.observeManagerNotes(rosterId).catch { emit(emptyMap()) }
        emitAll(combine(inner.observeShiftsForRoster(rosterId), notes, ::withManagerNotes).distinctUntilChanged())
    }

    override suspend fun getShiftsForRoster(rosterId: String): Result<List<Shift>> {
        if (!managesRoster(rosterId)) return inner.getShiftsForRoster(rosterId)
        return coroutineScope {
            val notes = async { inner.getManagerNotes(rosterId).getOrNull().orEmpty() }
            inner.getShiftsForRoster(rosterId).map { shifts -> withManagerNotes(shifts, notes.await()) }
        }
    }

    override suspend fun getShift(shiftId: String): Result<Shift> = inner.getShift(shiftId).map { shift ->
        if (!managesRoster(shift.rosterId)) return@map shift
        inner.getManagerNote(shiftId).getOrNull()?.applyTo(shift) ?: shift
    }
}
